import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Loader2, Layers } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

import planningAPI from "../services/planningService";
import { emptyForm, emptySlot } from "./schedule/scheduleManagerConstants";
import ScheduleManagerLayout from "./schedule/ScheduleManagerLayout";
import ScheduleFormDialog from "./schedule/ScheduleFormDialog";
import ScheduleSlotDialog from "./schedule/ScheduleSlotDialog";
import ScheduleDeleteDialog from "./schedule/ScheduleDeleteDialog";

const ScheduleManager = () => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteHasAssignments, setDeleteHasAssignments] = useState(false);
  const [deleteAssignmentMessage, setDeleteAssignmentMessage] = useState('');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [editSlotId, setEditSlotId] = useState(null);
  const [editingScheduleId, setEditingScheduleId] = useState(null);

  const [scheduleForm, setScheduleForm] = useState(emptyForm);
  const [timeSlotForm, setTimeSlotForm] = useState(emptySlot);

  useEffect(() => { loadSchedules(); }, []);

  useEffect(() => {
    if (selectedScheduleId) loadScheduleDetails(selectedScheduleId);
  }, [selectedScheduleId]);

  const loadSchedules = async (options = {}) => {
    const { selectId } = options;
    try {
      setLoading(true);
      const data = await planningAPI.schedules.getSchedules({ limit: 200 });
      const items = data.items || [];
      setSchedules(items);
      if (items.length > 0) {
        const nextSelected =
          selectId ??
          (items.some((item) => item.id === selectedScheduleId) ? selectedScheduleId : null) ??
          (items[0] ? items[0].id : null);
        setSelectedScheduleId(nextSelected);
      } else {
        setSelectedScheduleId(null);
        setSelectedSchedule(null);
      }
    } catch (error) {
      toast.error(t("planning.scheduleManager.errors.loadSchedules"));
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleDetails = async (scheduleId) => {
    try {
      const data = await planningAPI.schedules.getSchedule(scheduleId);
      setSelectedSchedule(data);
    } catch (error) {
      toast.error(t("planning.scheduleManager.errors.loadSchedule"));
    }
  };

  const handleOpenCreate = () => {
    setScheduleForm(emptyForm);
    setShowCreateModal(true);
    setEditingScheduleId(null);
  };

  const handleEditSchedule = () => {
    if (!selectedSchedule) return;
    setScheduleForm({
      name: selectedSchedule.name || "",
      description: selectedSchedule.description || "",
      schedule_type: selectedSchedule.schedule_type || "FIXED",
      cycle_type: selectedSchedule.cycle_type || "WEEKLY",
      hours_per_day: selectedSchedule.hours_per_day || 8,
      hours_per_week: selectedSchedule.hours_per_week || 40,
      break_duration_minutes: selectedSchedule.break_duration_minutes || 60,
      mandatory_breaks: selectedSchedule.mandatory_breaks ?? true,
      late_arrival_tolerance_minutes: selectedSchedule.late_arrival_tolerance_minutes ?? 15,
      early_departure_tolerance_minutes: selectedSchedule.early_departure_tolerance_minutes ?? 15,
      default_schedule_enabled: false,
      default_days: ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"],
      default_start_time: "08:00",
      default_end_time: "17:00",
      default_break_start: "12:00",
      default_break_end: "13:00",
      default_break_enabled: true,
    });
    setShowCreateModal(true);
    setEditingScheduleId(selectedSchedule.id);
  };

  const handleSaveSchedule = async () => {
    try {
      if (!scheduleForm.name.trim()) {
        toast.error(t("planning.scheduleManager.errors.nameRequired"));
        return;
      }

      const payload = {
        name: scheduleForm.name,
        description: scheduleForm.description,
        schedule_type: scheduleForm.schedule_type,
        cycle_type: scheduleForm.cycle_type,
        hours_per_day: Number(scheduleForm.hours_per_day) || 0,
        hours_per_week: Number(scheduleForm.hours_per_week) || 0,
        break_duration_minutes: Number(scheduleForm.break_duration_minutes) || 0,
        mandatory_breaks: scheduleForm.mandatory_breaks,
        late_arrival_tolerance_minutes: Number(scheduleForm.late_arrival_tolerance_minutes) || 0,
        early_departure_tolerance_minutes: Number(scheduleForm.early_departure_tolerance_minutes) || 0,
      };

      let newScheduleId = editingScheduleId;

      if (editingScheduleId) {
        await planningAPI.schedules.updateSchedule(editingScheduleId, payload);
      } else {
        const created = await planningAPI.schedules.createSchedule(payload);
        newScheduleId = created.id;
      }

      if (!editingScheduleId && scheduleForm.default_schedule_enabled && newScheduleId) {
        const { default_days, default_start_time, default_end_time, default_break_start, default_break_end, default_break_enabled } = scheduleForm;
        const slotPayloads = default_days.map((day) => ({
          week_day: day,
          start_time: `${default_start_time}:00`,
          end_time: `${default_end_time}:00`,
          break_start: default_break_enabled && default_break_start ? `${default_break_start}:00` : null,
          break_end: default_break_enabled && default_break_end ? `${default_break_end}:00` : null,
        }));
        for (const slot of slotPayloads) {
          try {
            await planningAPI.schedules.addTimeSlot(newScheduleId, slot);
          } catch (error) {
          }
        }
      }

      setShowCreateModal(false);
      setEditingScheduleId(null);
      await loadSchedules({ selectId: newScheduleId ?? selectedScheduleId });
      if (newScheduleId ?? selectedScheduleId) {
        await loadScheduleDetails(newScheduleId ?? selectedScheduleId);
      }
    } catch (error) {
    }
  };

  const handleDeleteSchedule = async (force = false) => {
    if (!selectedSchedule) return;
    try {
      await planningAPI.schedules.deleteSchedule(selectedSchedule.id, force);
      setShowDeleteDialog(false);
      setDeleteHasAssignments(false);
      setDeleteAssignmentMessage('');
      setSelectedScheduleId(null);
      setSelectedSchedule(null);
      await loadSchedules();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || '';
      if (status === 400 && !force) {
        setDeleteHasAssignments(true);
        setDeleteAssignmentMessage(detail);
      } else {
      }
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteHasAssignments(false);
    setDeleteAssignmentMessage('');
    setShowDeleteDialog(true);
  };

  const handleOpenSlotModal = (slot = null) => {
    if (slot) {
      setEditSlotId(slot.id);
      setTimeSlotForm({
        week_day: slot.week_day,
        start_time: slot.start_time?.substring(0, 5) || "08:00",
        end_time: slot.end_time?.substring(0, 5) || "17:00",
        break_start: slot.break_start?.substring(0, 5) || "12:00",
        break_end: slot.break_end?.substring(0, 5) || "13:00",
        break_enabled: !!(slot.break_start && slot.break_end),
      });
    } else {
      setEditSlotId(null);
      setTimeSlotForm(emptySlot);
    }
    setShowSlotModal(true);
  };

  const handleSaveTimeSlot = async () => {
    if (!selectedSchedule) { toast.error(t("planning.scheduleManager.errors.selectSchedule")); return; }
    const { week_day, start_time, end_time, break_start, break_end, break_enabled } = timeSlotForm;
    if (!week_day || !start_time || !end_time) { toast.error(t("planning.scheduleManager.errors.slotRequired")); return; }
    const payload = {
      week_day,
      start_time: `${start_time}:00`,
      end_time: `${end_time}:00`,
      break_start: break_enabled && break_start ? `${break_start}:00` : null,
      break_end: break_enabled && break_end ? `${break_end}:00` : null,
    };
    try {
      if (editSlotId) {
        await planningAPI.schedules.updateTimeSlot(editSlotId, payload);
      } else {
        await planningAPI.schedules.addTimeSlot(selectedSchedule.id, payload);
      }
      setShowSlotModal(false);
      await loadScheduleDetails(selectedSchedule.id);
    } catch (error) {
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await planningAPI.schedules.deleteTimeSlot(slotId);
      await loadScheduleDetails(selectedSchedule.id);
    } catch (error) {
    }
  };

  const displaySchedules = useMemo(
    () => schedules.map((s) => ({ ...s, total_slots: s.time_slots?.length || 0 })),
    [schedules]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("planning.scheduleManager.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("planning.scheduleManager.subtitle")}</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" /> {t("planning.scheduleManager.newSchedule")}
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            {t("planning.scheduleManager.loading")}
          </CardContent>
        </Card>
      ) : displaySchedules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Layers className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            {t("planning.scheduleManager.empty")}
          </CardContent>
        </Card>
      ) : (
        <ScheduleManagerLayout
          displaySchedules={displaySchedules}
          selectedScheduleId={selectedScheduleId}
          setSelectedScheduleId={setSelectedScheduleId}
          selectedSchedule={selectedSchedule}
          handleEditSchedule={handleEditSchedule}
          handleOpenDeleteDialog={handleOpenDeleteDialog}
          handleOpenSlotModal={handleOpenSlotModal}
          handleDeleteSlot={handleDeleteSlot}
        />
      )}

      <ScheduleFormDialog
        open={showCreateModal}
        onOpenChange={(open) => { setShowCreateModal(open); if (!open) setEditingScheduleId(null); }}
        editingScheduleId={editingScheduleId}
        scheduleForm={scheduleForm}
        setScheduleForm={setScheduleForm}
        handleSaveSchedule={handleSaveSchedule}
      />

      <ScheduleSlotDialog
        open={showSlotModal}
        onOpenChange={setShowSlotModal}
        editSlotId={editSlotId}
        timeSlotForm={timeSlotForm}
        setTimeSlotForm={setTimeSlotForm}
        handleSaveTimeSlot={handleSaveTimeSlot}
      />

      <ScheduleDeleteDialog
        open={showDeleteDialog}
        onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) { setDeleteHasAssignments(false); setDeleteAssignmentMessage(''); } }}
        deleteHasAssignments={deleteHasAssignments}
        deleteAssignmentMessage={deleteAssignmentMessage}
        handleDeleteSchedule={handleDeleteSchedule}
      />
    </div>
  );
};

export default ScheduleManager;
